import {schemeCategory10} from "d3-scale-chromatic";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,} from "recharts";
import * as _ from "lodash";
import "./SentimentOverTimeTrellisGraph.css";
import moment from "moment";
import {useGetSentimentsBreakdownMutation} from "../../../store/api/lumi/sentiment";
import {LoadingOutlined} from "@ant-design/icons";
import {Spin} from "antd";
import {getConceptFilter} from "../../../utils/getConceptFilter";
import {formatDateToUtc} from "../../../utils/formatDate";

export function SentimentOverTimeTrellisGraphs(props) {
    const {
        id,
        filters,
        includeExamples,
        selectedDateName,
        interval,
        setFilterDocCount,
    } = props;
    const [data, setData] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [opacity, setOpacity] = useState([]);
    const [loading, setLoading] = useState(false);
    const [defaultConcepts, setDefaultConcepts] = useState([]);
    const [getSentimentsBreakdown] = useGetSentimentsBreakdownMutation();

    const conceptFilter = useMemo(
        () => JSON.stringify(getConceptFilter(props)),
        [props]
    );

    const getSentimentData = useCallback(async () => {
        setLoading(true);

        const { data } = await getSentimentsBreakdown({
            id,
            concept_selector: JSON.parse(conceptFilter),
            filter: filters,
            include_examples: includeExamples,
            breakdowns: [{ name: selectedDateName, interval }],
        });
        setFilterDocCount(data.filter_count);
        setData(data.breakdowns?.[0].buckets);
        const uniqueConcepts = data.match_counts
            .map((item) => item.name)
            .slice(0, 3);
        setDefaultConcepts(uniqueConcepts);
        setLoading(false);
    }, [
        getSentimentsBreakdown,
        id,
        filters,
        includeExamples,
        interval,
        selectedDateName,
        conceptFilter,
        setFilterDocCount,
    ]);

    useEffect(() => {
        setLoading(true);
        const debouncedGetSentiment = _.debounce(getSentimentData, 1000);

        debouncedGetSentiment();

        return () => {
            debouncedGetSentiment.cancel();
        };
    }, [getSentimentData]);

    const generateColor = (concept, index, totalConcepts) => {
        if (index < schemeCategory10.length) {
            return schemeCategory10[index];
        } else {
            // Generate a unique color for concepts beyond the predefined set
            let hue = index * 137.508; // Use golden angle approximation
            return `hsl(${hue % 360}, 55%, 75%)`;
        }
    };

    const conceptColors = useMemo(() => {
        const colors = {};
        const conceptsToDisplay =
            props.selectedLocalConcepts.length > 0
                ? props.selectedLocalConcepts
                : defaultConcepts;
        conceptsToDisplay.forEach((concept, index) => {
            colors[concept] = generateColor(
                concept,
                index,
                conceptsToDisplay.length
            );
        });
        return colors;
    }, [props.selectedLocalConcepts, defaultConcepts]);

    function calculateSentimentEffect(negative, neutral, positive) {
        // Define the weights for negative, neutral, and positive sentiments
        const weightNegative = -1; // Negative sentiment has a negative impact
        const weightNeutral = 0; // Neutral sentiment has no impact
        const weightPositive = 1; // Positive sentiment has a positive impact

        // Calculate the sentiment effect
        const sentimentEffect =
            negative * weightNegative +
            neutral * weightNeutral +
            positive * weightPositive;

        return Math.trunc(sentimentEffect * 100) / 100;
    }

    useEffect(() => {
        if (data?.length) {
            const preparedData = data.map(({ label, match_counts,filter_count }) => {
                if (!filter_count) return
                const formattedDate = formatDateToUtc(label, props.interval);
                const dateData = { iteration: formattedDate };

                match_counts.forEach((item) => {
                    dateData[`${item.name}_effect`] = calculateSentimentEffect(
                        item.sentiment_share.negative,
                        item.sentiment_share.neutral,
                        item.sentiment_share.positive
                    );
                    dateData[`${item.name}_positive`] =
                        item.sentiment_share.positive || 0;
                    dateData[`${item.name}_neutral`] =
                        item.sentiment_share.neutral || 0;
                    dateData[`${item.name}_negative`] =
                        0 - item.sentiment_share.negative || 0;
                    dateData[`${item.name}`] = item.sentiment_counts.total || 0;
                });

                return dateData;
            });

            setChartData(
                preparedData?.filter(value => value !== undefined).sort((e, f) =>
                    moment(e.iteration).isBefore(moment(f.iteration)) ? -1 : 1
                )
            );
        }
    }, [
        data,
        props.selectedLocalConcepts,
        props.selectedFieldValue,
        props.selectedFieldName,
    ]);

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            // You can access more data from the payload if needed
            return (
                <div
                    className="custom-tooltip"
                    style={{
                        backgroundColor: "#ffffff",
                        padding: "5px",
                        border: "1px solid #cccccc",
                        borderRadius: "5px",
                    }}
                >
                    <p>Date: {data.iteration}</p>
                    {/* Displaying the sentiment effect and other info based on the hovered line */}
                    {payload.map((item, index) => (
                        <p key={index} style={{ color: item.color }}>
                            {item.name.replace("_effect", "")}: {item.value}
                        </p>
                    ))}
                </div>
            );
        }

        return null;
    };

    const CustomizedLabel = ({ x, y, stroke, value }) => {
        return (
            <text
                key={x + y}
                x={x}
                y={y}
                dy={-4}
                fill={stroke}
                fontSize={10}
                textAnchor="middle"
            >
                {JSON.stringify(_.round(value, 2))}
            </text>
        );
    };

    const handleMouseEnter = (o) => {
        const { dataKey } = o;

        setOpacity({ opacity: { ...opacity, [dataKey]: 1 } });
    };

    const handleMouseLeave = (o) => {
        const { dataKey } = o;
        setOpacity({ opacity: { ...opacity, [dataKey]: 0.5 } });
    };

    const CustomYAxisLabel = ({ viewBox, value }) => {
        const { y } = viewBox;
        return (
            <text
                x={75}
                y={y}
                dy={50}
                fill="#3c3c3c"
                fontSize="14"
                fontWeight="bold"
                textAnchor={"middle"}
            >
                {value}
            </text>
        );
    };

    const conceptsToDisplay =
        props.selectedLocalConcepts.length > 0
            ? props.selectedLocalConcepts
            : defaultConcepts;

    return (
        <div
            className="sentiment-bars-container-net"
            style={{ overflow: "auto", height: 350 }}
        >
            {conceptsToDisplay.length > 0 ? (
                conceptsToDisplay.map((concept, index, arr) => {
                    const color = conceptColors[concept] || "#8884d8";
                    const defaultColor = "#8884d8";
                    return (
                        <React.Fragment
                            key={concept + index + index + "sentiment"}
                        >
                            <ResponsiveContainer height="100%" minHeight={120}>
                                <LineChart
                                    data={chartData}
                                    margin={{
                                        top: arr.length - 1 === index ? 0 : 0,
                                        right: 0,
                                        left: 0,
                                        bottom:
                                            arr.length - 1 === index ? 20 : 0,
                                    }}
                                    syncId="anyId"
                                >
                                    <Legend
                                        verticalAlign="top"
                                        height={20}
                                        wrapperStyle={{
                                            height: "20px",
                                            overflow: "hidden",
                                        }}
                                        onMouseEnter={handleMouseEnter}
                                        onMouseLeave={handleMouseLeave}
                                    />

                                    <XAxis
                                        allowDataOverflow
                                        domain={["auto", "auto"]}
                                        dataKey="iteration"
                                        hide={arr.length - 1 !== index}
                                        tick={{ fontSize: 16, color: "#000", fontWeight: "bold" }}
                                    >
                                        {/*<text*/}
                                        {/*    x={700}*/}
                                        {/*    y={50}*/}
                                        {/*    dy={30}*/}
                                        {/*    textAnchor="middle"*/}
                                        {/*    fontSize={11}*/}
                                        {/*>*/}
                                        {/*    Date*/}
                                        {/*</text>*/}
                                    </XAxis>
                                    <YAxis
                                        type="number"
                                        label={
                                            <CustomYAxisLabel value={concept} />
                                        }
                                        width={150}
                                        domain={[-1, 1.2]}
                                        allowDataOverflow
                                        tick={{ fontSize: 16, color: "#000", fontWeight: "bold" }}
                                    />

                                    <Tooltip content={CustomTooltip} />

                                    <Line
                                        dot={false}
                                        type="monotone"
                                        dataKey={`${concept}_effect`}
                                        stroke={color || defaultColor}
                                        fill={color || defaultColor}
                                        stackId="positive"
                                        activeDot={false}
                                        // label={CustomizedLabel}
                                        strokeWidth={2}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </React.Fragment>
                    );
                })
            ) : (
                <Spin
                    indicator={
                        <LoadingOutlined
                            style={{ fontSize: 24 }}
                            className={"spinner-by-center"}
                            spin
                        />
                    }
                    spinning={loading}
                >
                    <ResponsiveContainer height="100%" minHeight={150}>
                        <LineChart data={chartData} height={100} syncId="anyId">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                allowDataOverflow
                                dataKey="end_date"
                                interval={0}
                                padding={{ left: 30, right: 30 }}
                                tick={{ fontSize: 16, color: "#000", fontWeight: "bold" }}
                            >
                                {/*<text*/}
                                {/*    x={700}*/}
                                {/*    y={50}*/}
                                {/*    dy={30}*/}
                                {/*    textAnchor="middle"*/}
                                {/*    fontSize={11}*/}
                                {/*>*/}
                                {/*    Date*/}
                                {/*</text>*/}
                            </XAxis>
                            <YAxis
                                type="number"
                                allowDataOverflow
                                domain={[-1, 1.2]}
                                tick={{ fontSize: 16, color: "#000", fontWeight: "bold" }}
                            />
                            {chartData.map((e, index) => {
                                return (
                                    <Line
                                        dot={false}
                                        key={index}
                                        type="monotone"
                                        stackId="positive"
                                        activeDot={false}
                                        // label={CustomizedLabel}
                                        strokeWidth={2}
                                    />
                                );
                            })}
                        </LineChart>
                    </ResponsiveContainer>
                </Spin>
            )}
        </div>
    );
}
